import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {distinctUntilChanged, Subject, takeUntil} from 'rxjs';
import {MaterialModule} from '../../../material.module';
import {
    Webinar,
    WebinarAnalyticsResponse,
    WebinarChatModerationAction,
    WebinarChatMessage,
    WebinarEngagementInput,
    WebinarEngagementItem,
    WebinarInput,
    WebinarRegistration,
    WebinarSeries,
    WebinarSeriesInput,
} from '../../../models/webinar.model';
import {WebinarsService} from '../../../services/webinars/webinars.service';
import {environment} from '../../../../environments/environment';
import {WebinarsDetailComponent} from '../webinars-detail/webinars-detail.component';
import {formatHttpErrorMessage} from '../../../services/connect/http-error-message';
import {ToastService} from '../../../services/layout/toast.service';

@Component({
    selector: 'app-webinar-detail-page',
    standalone: true,
    imports: [ReactiveFormsModule, MaterialModule, WebinarsDetailComponent],
    templateUrl: './webinar-detail-page.component.html',
    styleUrls: ['./webinar-detail-page.component.scss'],
    host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarDetailPageComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly webinarsService = inject(WebinarsService);
    private readonly toastService = inject(ToastService);

    form!: FormGroup;
    selectedWebinar: Webinar | null = null;
    registrations: WebinarRegistration[] = [];
    registrationColumns = ['name', 'email', 'registeredAt', 'source'];
    chatMessages: WebinarChatMessage[] = [];
    pinnedChatMessageId: string | null = null;
    isChatLoading = false;
    webinarAnalytics: WebinarAnalyticsResponse | null = null;
    isAnalyticsLoading = false;
    analyticsErrorMessage: string | null = null;
    engagementItems: WebinarEngagementItem[] = [];
    isEngagementLoading = false;
    recurrencePreviewOccurrences: Webinar[] = [];
    recurrencePreviewSeries: WebinarSeries | null = null;
    isRecurrencePreviewLoading = false;
    recurrencePreviewError: string | null = null;

    webinarsBaseUrl = environment.webinarsServerUrl.replace(/\/$/, '');

    isLoading = false;
    isSaving = false;
    hasHostPassword = false;
    hostPasswordMasked = false;
    private readonly hostPasswordPlaceholder = '********';
    private readonly defaultSpeakerBadgeLabel = 'Foredragsholder';
    private readonly maxSpeakerMembers = 50;
    private readonly positiveIntegerValidators = [
        Validators.required,
        Validators.min(1),
        WebinarDetailPageComponent.integerValidator,
    ];

    private destroy$ = new Subject<void>();
    private currentId: string | null = null;
    private allWebinars: Webinar[] = [];
    private existingSlugs = new Set<string>();
    private slugManuallyEdited = false;
    private slugSyncInProgress = false;

    ngOnInit(): void {
        this.buildForm();

        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const id = params.get('id');
            this.currentId = id;
            this.refreshSlugSet();
            if (id) {
                this.loadWebinar(id);
            } else {
                this.loadExistingSlugs();
                this.startCreate();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    save(): void {
        if (this.form.invalid || this.isSaving) {
            this.form.markAllAsTouched();
            return;
        }

        if (!this.selectedWebinar && this.form.get('webinarKind')?.value === 'recurring') {
            this.saveSeries();
            return;
        }

        const payload = this.buildPayload();
        if (!payload) return;

        this.isSaving = true;

        const request = this.selectedWebinar
            ? this.webinarsService.updateWebinar(this.selectedWebinar.id, payload)
            : this.webinarsService.createWebinar(payload);

        request.pipe(takeUntil(this.destroy$)).subscribe({
            next: (webinar) => {
                this.toastService.showSuccess('Webinar saved', 2500);
                this.selectedWebinar = webinar;
                const responseHasPassword = Boolean(
                    (webinar as any)?.hostPasswordHash || (webinar as any)?.hostPasswordSet || (webinar as any)?.hasHostPassword
                );
                const hostPasswordCleared = payload.hostPassword === null;
                const hasPassword = hostPasswordCleared ? false : Boolean(payload.hostPassword || responseHasPassword);
                this.hasHostPassword = hasPassword;
                this.hostPasswordMasked = hasPassword;
                this.upsertCachedWebinar(webinar);
                this.patchFormFromWebinar(webinar);
                const hostPasswordControl = this.form.get('hostPassword');
                if (hostPasswordControl) {
                    const newValue = this.hasHostPassword ? this.hostPasswordPlaceholder : '';
                    hostPasswordControl.setValue(hostPasswordCleared ? '' : newValue, {emitEvent: false});
                    hostPasswordControl.markAsPristine();
                    hostPasswordControl.markAsUntouched();
                }
                this.setFormPristine();
                this.currentId = webinar.id;
                this.slugManuallyEdited = true;
                this.refreshSlugSet();
                this.loadExistingSlugs();
                this.form.updateValueAndValidity();
                this.router.navigate(['/webinars', webinar.id], {replaceUrl: true});
                this.loadRegistrations(webinar.id);
                this.loadChats(webinar.id);
                this.loadAnalytics(webinar.id);
                this.loadEngagements(webinar.id);
                this.isSaving = false;
            },
            error: (err) => {
                console.error('Failed to save webinar', err);
                this.toastService.showError('Saving webinar failed', 3500);
                this.isSaving = false;
            },
        });
    }

    previewSeries(): void {
        if (this.selectedWebinar || this.isRecurrencePreviewLoading) return;
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.toastService.showError('Fix validation errors before previewing the series.', 3500);
            return;
        }
        const payload = this.buildSeriesPayload();
        if (!payload) return;

        this.isRecurrencePreviewLoading = true;
        this.recurrencePreviewError = null;
        this.webinarsService
            .previewWebinarSeries(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (preview) => {
                    this.recurrencePreviewSeries = preview.series;
                    this.recurrencePreviewOccurrences = preview.occurrences ?? [];
                    this.isRecurrencePreviewLoading = false;
                },
                error: (err) => {
                    console.error('Failed to preview webinar series', err);
                    this.recurrencePreviewSeries = null;
                    this.recurrencePreviewOccurrences = [];
                    this.recurrencePreviewError = this.formatSeriesError(err);
                    this.toastService.showError('Could not preview recurring series', 3500);
                    this.isRecurrencePreviewLoading = false;
                },
            });
    }

    delete(): void {
        if (!this.selectedWebinar || this.isSaving) return;
        this.isSaving = true;
        this.webinarsService
            .deleteWebinar(this.selectedWebinar.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.toastService.showSuccess('Webinar deleted', 2500);
                    this.router.navigate(['/webinars']);
                    this.isSaving = false;
                },
                error: (err) => {
                    console.error('Failed to delete webinar', err);
                    this.toastService.showError('Deleting webinar failed', 3500);
                    this.isSaving = false;
                },
            });
    }

    openPreview(): void {
        if (!this.selectedWebinar) return;
        const slug = encodeURIComponent(this.selectedWebinar.slug);
        window.open(`${this.webinarsBaseUrl}/login/${slug}`, '_blank');
    }

    private buildForm(): void {
        this.form = this.fb.group(
            {
                title: ['', Validators.required],
                companyName: ['', Validators.required],
                adminEmail: ['', [Validators.email]],
                speakerBadgeLabel: [this.defaultSpeakerBadgeLabel, [Validators.maxLength(40)]],
                speakers: this.fb.array([], [this.speakerMembersValidator]),
                registrationPageUrl: ['', [Validators.pattern(/^https?:\/\/.+/i)]],
                accessMode: ['register', Validators.required],
                showOnListing: [false],
                hostPassword: [''],
                slug: ['', [Validators.pattern(/^[a-z0-9-]*$/), this.slugUniqueValidator]],
                provider: ['youtube', Validators.required],
                videoCode: ['', Validators.required],
                scheduledDate: [null],
                scheduledTime: [''],
                endDate: [null],
                endTime: [''],
                showViewerCount: [true],
                showElapsedTime: [true],
                webinarKind: ['one_time'],
                timezone: [this.defaultTimezone()],
                recurrenceFrequency: ['weekly'],
                recurrenceInterval: [1],
                recurrenceDays: this.fb.group({
                    SU: [false],
                    MO: [false],
                    TU: [false],
                    WE: [false],
                    TH: [true],
                    FR: [false],
                    SA: [false],
                }),
                recurrenceStartsOn: [null],
                recurrenceLocalStartTime: ['10:00'],
                recurrenceDurationMinutes: [60],
                recurrenceEndCondition: ['count'],
                recurrenceCount: [4],
                recurrenceEndsOn: [null],
            },
            {validators: this.scheduleValidator},
        );

        this.observeTitleChanges();
        this.observeSlugChanges();
        this.observeWebinarKindChanges();
    }

    createEngagement(payload: WebinarEngagementInput): void {
        if (!this.selectedWebinar) return;
        this.webinarsService
            .createEngagement(this.selectedWebinar.id, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const label = payload.status === 'scheduled'
                        ? 'Scheduled engagement created'
                        : 'Engagement item created';
                    this.toastService.showSuccess(label, 2500);
                    this.loadEngagements(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to create engagement', err);
                    this.toastService.showError(this.formatEngagementError(err, 'Could not create engagement item'), 3500);
                },
            });
    }

    updateEngagement(update: { id: string; input: WebinarEngagementInput }): void {
        if (!this.selectedWebinar) return;
        this.webinarsService
            .updateEngagement(this.selectedWebinar.id, update.id, update.input)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    const label = update.input.status === 'cancelled'
                        ? 'Scheduled engagement cancelled'
                        : update.input.status === 'scheduled'
                            ? 'Scheduled engagement updated'
                            : 'Engagement item updated';
                    this.toastService.showSuccess(label, 2500);
                    this.loadEngagements(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to update engagement', err);
                    this.toastService.showError(this.formatEngagementError(err, 'Could not update engagement item'), 3500);
                },
            });
    }

    private loadRegistrations(id: string): void {
        this.webinarsService
            .getRegistrations(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (registrations) => {
                    this.registrations = registrations;
                },
                error: (err) => {
                    console.error('Failed to load registrations', err);
                    this.registrations = [];
                },
            });
    }

    private loadChats(id: string): void {
        this.isChatLoading = true;
        this.webinarsService
            .getWebinarChats(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (messages) => {
                    this.chatMessages = messages ?? [];
                    this.isChatLoading = false;
                },
                error: (err) => {
                    console.error('Failed to load webinar chats', err);
                    this.toastService.showError('Could not load chat history', 3500);
                    this.chatMessages = [];
                    this.isChatLoading = false;
                },
            });
    }

    private loadAnalytics(id: string): void {
        this.isAnalyticsLoading = true;
        this.analyticsErrorMessage = null;
        this.webinarsService
            .getWebinarAnalytics(id, {includeUsers: true, limit: 10})
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (analytics) => {
                    this.webinarAnalytics = analytics;
                    this.isAnalyticsLoading = false;
                },
                error: (err) => {
                    console.error('Failed to load webinar analytics', err);
                    this.webinarAnalytics = null;
                    this.analyticsErrorMessage = 'Refresh the panel or try again later.';
                    this.isAnalyticsLoading = false;
                },
            });
    }

    moderateChat(event: { message: WebinarChatMessage; action: WebinarChatModerationAction }): void {
        if (!this.selectedWebinar || !event?.message?.id || !event.action) return;
        this.webinarsService
            .moderateChatMessage(this.selectedWebinar.id, event.message.id, event.action)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.pinnedChatMessageId = result.pinnedMessageId;
                    const label = this.formatModerationAction(event.action);
                    this.toastService.showSuccess(`Chat message ${label}`, 2500);
                    this.loadChats(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to moderate chat message', err);
                    this.toastService.showError('Could not moderate chat message', 3500);
                },
            });
    }

    deleteEngagement(id: string): void {
        if (!this.selectedWebinar) return;
        this.webinarsService
            .deleteEngagement(this.selectedWebinar.id, id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.toastService.showSuccess('Engagement item deleted', 2000);
                    this.loadEngagements(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to delete engagement', err);
                    this.toastService.showError('Could not delete engagement item', 3500);
                },
            });
    }

    publishEngagement(id: string): void {
        if (!this.selectedWebinar) return;
        this.webinarsService
            .publishEngagement(this.selectedWebinar.id, id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.toastService.showSuccess('Engagement item published', 2500);
                    this.loadEngagements(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to publish engagement', err);
                    if (err?.status === 429 && err?.error?.retryIn) {
                        const seconds = Math.ceil(Number(err.error.retryIn) / 1000);
                        this.toastService.showInfo(`Please wait ${seconds}s before publishing another item`, 3500);
                    } else if (err?.status === 409 && err?.error?.error === 'engagement_locked') {
                        this.toastService.showError('This engagement can no longer be published from admin.', 3500);
                    } else {
                        this.toastService.showError('Could not publish engagement item', 3500);
                    }
                },
            });
    }

    closeEngagement(id: string): void {
        if (!this.selectedWebinar) return;
        this.webinarsService
            .closeEngagement(this.selectedWebinar.id, id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.toastService.showSuccess('Poll closed', 2500);
                    this.loadEngagements(this.selectedWebinar!.id);
                },
                error: (err) => {
                    console.error('Failed to close engagement', err);
                    this.toastService.showError('Could not close poll', 3500);
                },
            });
    }

    refreshEngagements(): void {
        if (!this.selectedWebinar) return;
        this.loadEngagements(this.selectedWebinar.id);
    }

    refreshAnalytics(): void {
        if (!this.selectedWebinar) return;
        this.loadAnalytics(this.selectedWebinar.id);
    }

    private startCreate(): void {
        this.selectedWebinar = null;
        this.registrations = [];
        this.chatMessages = [];
        this.pinnedChatMessageId = null;
        this.isChatLoading = false;
        this.webinarAnalytics = null;
        this.isAnalyticsLoading = false;
        this.analyticsErrorMessage = null;
        this.engagementItems = [];
        this.isEngagementLoading = false;
        this.recurrencePreviewOccurrences = [];
        this.recurrencePreviewSeries = null;
        this.recurrencePreviewError = null;
        this.isRecurrencePreviewLoading = false;
        this.hasHostPassword = false;
        this.hostPasswordMasked = false;
        this.form.reset({
            title: '',
            companyName: '',
            adminEmail: '',
            registrationPageUrl: '',
            accessMode: 'register',
            showOnListing: false,
            hostPassword: '',
            speakerBadgeLabel: this.defaultSpeakerBadgeLabel,
            speakers: [],
            slug: '',
            provider: 'youtube',
            videoCode: '',
            scheduledDate: null,
            scheduledTime: '',
            endDate: null,
            endTime: '',
            showViewerCount: true,
            showElapsedTime: true,
            webinarKind: 'one_time',
            timezone: this.defaultTimezone(),
            recurrenceFrequency: 'weekly',
            recurrenceInterval: 1,
            recurrenceDays: {
                SU: false,
                MO: false,
                TU: false,
                WE: false,
                TH: true,
                FR: false,
                SA: false,
            },
            recurrenceStartsOn: null,
            recurrenceLocalStartTime: '10:00',
            recurrenceDurationMinutes: 60,
            recurrenceEndCondition: 'count',
            recurrenceCount: 4,
            recurrenceEndsOn: null,
        });
        this.replaceSpeakerMembers([]);
        this.slugManuallyEdited = false;
        this.refreshSlugSet();
        this.setFormPristine();
    }

    private loadWebinar(id: string): void {
        this.isLoading = true;
        this.pinnedChatMessageId = null;
        this.webinarsService
            .getWebinar(id, this.route.snapshot?.queryParamMap?.get('clientId'))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (webinar) => {
                    this.loadExistingSlugs();
                    this.selectedWebinar = webinar;
                    this.upsertCachedWebinar(webinar);
                    this.patchFormFromWebinar(webinar);
                    this.setFormPristine();
                    this.slugManuallyEdited = true;
                    this.refreshSlugSet();
                    this.form.updateValueAndValidity();
                    this.loadRegistrations(id);
                    this.loadChats(id);
                    this.loadAnalytics(id);
                    this.loadEngagements(id);
                    this.isLoading = false;
                },
                error: (err) => {
                    console.error('Failed to load webinar', err);
                    this.toastService.showError(err?.message === 'webinar_history_account_mismatch'
                        ? 'Select the account linked to this webinar in the account selector, then reopen the link.'
                        : 'Could not load webinar', 6000);
                    this.router.navigate(['/webinars']);
                    this.chatMessages = [];
                    this.pinnedChatMessageId = null;
                    this.isChatLoading = false;
                    this.webinarAnalytics = null;
                    this.isAnalyticsLoading = false;
                    this.analyticsErrorMessage = null;
                    this.engagementItems = [];
                    this.isEngagementLoading = false;
                    this.recurrencePreviewOccurrences = [];
                    this.recurrencePreviewSeries = null;
                    this.recurrencePreviewError = null;
                    this.isRecurrencePreviewLoading = false;
                    this.isLoading = false;
                },
            });
    }

    private loadEngagements(id: string): void {
        this.isEngagementLoading = true;
        this.webinarsService
            .getEngagements(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (items) => {
                    this.engagementItems = items ?? [];
                    this.isEngagementLoading = false;
                },
                error: (err) => {
                    console.error('Failed to load engagements', err);
                    this.toastService.showError('Could not load engagement items', 3500);
                    this.engagementItems = [];
                    this.isEngagementLoading = false;
                },
            });
    }

    private buildPayload(): WebinarInput | null {
        const raw = this.form.value;
        const scheduledAt = this.combineDateAndTime(raw.scheduledDate, raw.scheduledTime);
        const endAt = this.combineDateAndTime(raw.endDate, raw.endTime);
        const addons = {
            showViewerCount: raw.showViewerCount !== false,
            showElapsedTime: raw.showElapsedTime !== false,
        };

        if (scheduledAt && endAt && new Date(endAt).getTime() <= new Date(scheduledAt).getTime()) {
            this.toastService.showError('End time must be after the start time.', 3500);
            return null;
        }

        const slug = this.normaliseSlug(raw.slug);
        const companyName = String(raw.companyName ?? '').trim();
        if (!companyName) {
            this.toastService.showError('Company name is required.', 3000);
            return null;
        }
        const adminEmail = String(raw.adminEmail ?? '').trim();
        const registrationPageUrl = String(raw.registrationPageUrl ?? '').trim();
        const speakerBadgeLabel = String(raw.speakerBadgeLabel ?? '').trim() || this.defaultSpeakerBadgeLabel;
        const speakerMembers = Array.isArray(raw.speakers) ? raw.speakers : [];
        const rawHostPassword = typeof raw.hostPassword === 'string' ? raw.hostPassword : '';
        const trimmedHost = rawHostPassword.trim();
        const showOnListing = raw.showOnListing === true;
        let hostPassword: string | null | undefined;
        const hasExistingPassword = this.hasHostPassword || this.hostPasswordMasked;
        const accessMode = typeof raw.accessMode === 'string' && raw.accessMode ? raw.accessMode : 'register';
        if (hasExistingPassword) {
            if (trimmedHost === this.hostPasswordPlaceholder) {
                hostPassword = undefined; // keep existing
            } else if (!trimmedHost) {
                hostPassword = null; // clear existing
            } else {
                hostPassword = trimmedHost; // replace existing
            }
        } else {
            hostPassword = trimmedHost || undefined;
        }

        return {
            title: String(raw.title ?? '').trim(),
            companyName,
            adminEmail: adminEmail || null,
            speakers: {
                badgeLabel: speakerBadgeLabel,
                members: speakerMembers
                    .map((member: any) => ({
                        email: String(member?.email ?? '').trim().toLowerCase(),
                        name: String(member?.name ?? '').trim(),
                    }))
                    .filter((member: {email: string}) => Boolean(member.email))
                    .map((member: {email: string; name: string}) => ({
                        email: member.email,
                        ...(member.name ? {name: member.name} : {}),
                    })),
            },
            registrationPageUrl: registrationPageUrl || null,
            accessMode,
            showOnListing,
            hostPassword,
            slug: slug || undefined,
            provider: raw.provider,
            videoCode: String(raw.videoCode ?? '').trim(),
            scheduledAt,
            endAt,
            addons,
        };
    }

    private buildSeriesPayload(): WebinarSeriesInput | null {
        const raw = this.form.value;
        const template = this.buildPayloadTemplate();
        if (!template) return null;

        const startsOn = this.toPlainDate(raw.recurrenceStartsOn);
        if (!startsOn) {
            this.toastService.showError('Start date is required for a recurring series.', 3500);
            return null;
        }

        const timezone = String(raw.timezone ?? '').trim();
        if (!timezone) {
            this.toastService.showError('Timezone is required for a recurring series.', 3500);
            return null;
        }

        const localStartTime = String(raw.recurrenceLocalStartTime ?? '').trim();
        if (!/^\d{2}:\d{2}$/.test(localStartTime)) {
            this.toastService.showError('Start time is required for a recurring series.', 3500);
            return null;
        }

        const interval = this.readPositiveIntegerControl('recurrenceInterval', 'Interval');
        const durationMinutes = this.readPositiveIntegerControl('recurrenceDurationMinutes', 'Duration');
        if (interval === null || durationMinutes === null) return null;
        const frequency = raw.recurrenceFrequency === 'daily' || raw.recurrenceFrequency === 'monthly'
            ? raw.recurrenceFrequency
            : 'weekly';
        const recurrenceDays = raw.recurrenceDays && typeof raw.recurrenceDays === 'object'
            ? raw.recurrenceDays
            : {};
        const daysOfWeek = frequency === 'weekly'
            ? (['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'] as const).filter((day) => recurrenceDays[day] === true)
            : null;

        if (frequency === 'weekly' && !daysOfWeek?.length) {
            this.toastService.showError('Select at least one weekday for a weekly series.', 3500);
            return null;
        }

        const endCondition = raw.recurrenceEndCondition === 'date' ? 'date' : 'count';
        const count = endCondition === 'count' ? this.toPositiveInteger(raw.recurrenceCount, 0) : null;
        const endsOn = endCondition === 'date' ? this.toPlainDate(raw.recurrenceEndsOn) : null;

        if (endCondition === 'count' && !count) {
            this.toastService.showError('Occurrence count is required for a recurring series.', 3500);
            return null;
        }

        if (endCondition === 'date' && !endsOn) {
            this.toastService.showError('End date is required for a recurring series.', 3500);
            return null;
        }

        return {
            title: String(raw.title ?? '').trim(),
            slug: this.normaliseSlug(raw.slug) || undefined,
            timezone,
            recurrence: {
                frequency,
                interval,
                daysOfWeek,
                localStartTime,
                durationMinutes,
                startsOn,
                endsOn,
                count,
            },
            template,
        };
    }

    private buildPayloadTemplate(): WebinarSeriesInput['template'] | null {
        const raw = this.form.value;
        const companyName = String(raw.companyName ?? '').trim();
        if (!companyName) {
            this.toastService.showError('Company name is required.', 3000);
            return null;
        }
        const title = String(raw.title ?? '').trim();
        if (!title) {
            this.toastService.showError('Title is required.', 3000);
            return null;
        }
        const adminEmail = String(raw.adminEmail ?? '').trim();
        const registrationPageUrl = String(raw.registrationPageUrl ?? '').trim();
        const speakerBadgeLabel = String(raw.speakerBadgeLabel ?? '').trim() || this.defaultSpeakerBadgeLabel;
        const speakerMembers = Array.isArray(raw.speakers) ? raw.speakers : [];
        const rawHostPassword = typeof raw.hostPassword === 'string' ? raw.hostPassword : '';
        const trimmedHost = rawHostPassword.trim();
        const accessMode = typeof raw.accessMode === 'string' && raw.accessMode ? raw.accessMode : 'register';

        return {
            companyName,
            adminEmail: adminEmail || null,
            speakers: {
                badgeLabel: speakerBadgeLabel,
                members: speakerMembers
                    .map((member: any) => ({
                        email: String(member?.email ?? '').trim().toLowerCase(),
                        name: String(member?.name ?? '').trim(),
                    }))
                    .filter((member: {email: string}) => Boolean(member.email))
                    .map((member: {email: string; name: string}) => ({
                        email: member.email,
                        ...(member.name ? {name: member.name} : {}),
                    })),
            },
            registrationPageUrl: registrationPageUrl || null,
            accessMode,
            showOnListing: raw.showOnListing === true,
            hostPassword: trimmedHost || undefined,
            provider: raw.provider,
            videoCode: String(raw.videoCode ?? '').trim() || null,
            addons: {
                showViewerCount: raw.showViewerCount !== false,
                showElapsedTime: raw.showElapsedTime !== false,
            },
        };
    }

    private saveSeries(): void {
        const payload = this.buildSeriesPayload();
        if (!payload) return;

        this.isSaving = true;
        this.webinarsService
            .createWebinarSeries(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (created) => {
                    this.toastService.showSuccess('Recurring webinar series saved', 2500);
                    this.isSaving = false;
                    const seriesId = created.series.id || created.occurrences?.[0]?.seriesId;
                    if (seriesId) {
                        this.router.navigate(['/webinars', 'series', seriesId]);
                    } else {
                        this.router.navigate(['/webinars']);
                    }
                },
                error: (err) => {
                    console.error('Failed to save recurring webinar series', err);
                    this.toastService.showError(this.formatSeriesError(err), 3500);
                    this.isSaving = false;
                },
            });
    }

    private patchFormFromWebinar(webinar: Webinar): void {
        this.slugSyncInProgress = true;
        this.replaceSpeakerMembers([]);
        this.hasHostPassword = Boolean(
            webinar.hasHostPassword ?? (webinar as any)?.hostPasswordSet ?? (webinar as any)?.hostPasswordHash
        );
        this.hostPasswordMasked = this.hasHostPassword;
        this.form.reset({
            title: webinar.title,
            companyName: webinar.companyName || (webinar as any)?.clientName || webinar.clientId || '',
            adminEmail: webinar.adminEmail || '',
            speakerBadgeLabel: webinar.speakers?.badgeLabel || this.defaultSpeakerBadgeLabel,
            speakers: [],
            registrationPageUrl: webinar.registrationPageUrl || '',
            accessMode: webinar.accessMode || 'register',
            showOnListing: webinar.showOnListing === true,
            hostPassword: this.hasHostPassword ? this.hostPasswordPlaceholder : '',
            slug: webinar.slug,
            provider: webinar.provider,
            videoCode: webinar.videoCode,
            scheduledDate: this.parseDateOnly(webinar.scheduledAt),
            scheduledTime: this.formatTime(webinar.scheduledAt),
            endDate: this.parseDateOnly(webinar.endAt),
            endTime: this.formatTime(webinar.endAt),
            showViewerCount: webinar.addons?.showViewerCount !== false,
            showElapsedTime: webinar.addons?.showElapsedTime !== false,
            webinarKind: 'one_time',
            timezone: webinar.timezone || this.defaultTimezone(),
            recurrenceFrequency: 'weekly',
            recurrenceInterval: 1,
            recurrenceDays: {
                SU: false,
                MO: false,
                TU: false,
                WE: false,
                TH: true,
                FR: false,
                SA: false,
            },
            recurrenceStartsOn: null,
            recurrenceLocalStartTime: webinar.localStartTime || '10:00',
            recurrenceDurationMinutes: webinar.durationMinutes || 60,
            recurrenceEndCondition: 'count',
            recurrenceCount: 4,
            recurrenceEndsOn: null,
        });
        this.replaceSpeakerMembers(webinar.speakers?.members || []);
        this.slugSyncInProgress = false;
    }

    private get speakerMembersArray(): FormArray {
        return this.form.get('speakers') as FormArray;
    }

    private createSpeakerMemberGroup(member?: {email?: string | null; name?: string | null}): FormGroup {
        return this.fb.group({
            email: [member?.email || '', [Validators.required, Validators.email]],
            name: [member?.name || '', [Validators.maxLength(64)]],
        });
    }

    private replaceSpeakerMembers(members: Array<{email?: string | null; name?: string | null}>): void {
        const speakers = this.speakerMembersArray;
        speakers.clear({emitEvent: false});
        members.slice(0, this.maxSpeakerMembers).forEach((member) => {
            speakers.push(this.createSpeakerMemberGroup(member), {emitEvent: false});
        });
        speakers.updateValueAndValidity({emitEvent: false});
    }

    private observeTitleChanges(): void {
        const titleControl = this.form.get('title');
        const slugControl = this.form.get('slug');
        if (!titleControl || !slugControl) return;

        titleControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((value) => {
            if (this.slugManuallyEdited && slugControl.value) return;

            const autoSlug = this.normaliseSlug(value);
            this.slugSyncInProgress = true;
            slugControl.setValue(autoSlug, {emitEvent: false});
            this.slugSyncInProgress = false;
            slugControl.markAsPristine();
            slugControl.markAsUntouched();
            slugControl.updateValueAndValidity({onlySelf: true});
        });
    }

    private observeSlugChanges(): void {
        const slugControl = this.form.get('slug');
        if (!slugControl) return;

        slugControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((value) => {
            if (this.slugSyncInProgress) return;
            const normalised = this.normaliseSlug(value);
            if (value !== normalised) {
                this.slugSyncInProgress = true;
                slugControl.setValue(normalised, {emitEvent: false});
                this.slugSyncInProgress = false;
            }
            this.slugManuallyEdited = Boolean(normalised);
            slugControl.updateValueAndValidity({onlySelf: true});
        });
    }

    private observeWebinarKindChanges(): void {
        const kindControl = this.form.get('webinarKind');
        const videoCodeControl = this.form.get('videoCode');
        if (!kindControl || !videoCodeControl) return;

        kindControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((value) => {
            if (value === 'recurring' && !this.selectedWebinar) {
                videoCodeControl.clearValidators();
                this.setRecurrenceIntegerValidators(true);
            } else {
                videoCodeControl.setValidators([Validators.required]);
                this.setRecurrenceIntegerValidators(false);
            }
            videoCodeControl.updateValueAndValidity({emitEvent: false});
            this.recurrencePreviewOccurrences = [];
            this.recurrencePreviewSeries = null;
            this.recurrencePreviewError = null;
        });
    }

    private setRecurrenceIntegerValidators(enabled: boolean): void {
        ['recurrenceInterval', 'recurrenceDurationMinutes'].forEach((name) => {
            const control = this.form.get(name);
            if (!control) return;
            if (enabled) {
                control.setValidators(this.positiveIntegerValidators);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity({emitEvent: false});
        });
    }

    private normaliseSlug(value: unknown): string {
        if (value === null || value === undefined) return '';
        return String(value)
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 120);
    }

    private combineDateAndTime(date: Date | null, time: string | null | undefined): string | null {
        if (!date) return null;
        const safeTime = (time || '').trim();
        const [hours, minutes] = safeTime.split(':').map((part) => Number.parseInt(part, 10));
        const combined = new Date(date);
        combined.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
        return Number.isNaN(combined.valueOf()) ? null : combined.toISOString();
    }

    private toPlainDate(date: Date | null | undefined): string | null {
        if (!date) return null;
        const value = date instanceof Date ? date : new Date(date);
        if (Number.isNaN(value.valueOf())) return null;
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private static integerValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null || value === undefined || value === '') return null;
        const parsed = Number(value);
        return Number.isInteger(parsed) ? null : {integer: true};
    }

    private readPositiveIntegerControl(controlName: string, label: string): number | null {
        const control = this.form.get(controlName);
        if (!control || control.invalid) {
            this.toastService.showError(`${label} must be a whole number greater than 0.`, 3500);
            return null;
        }
        const parsed = Number(control.value);
        if (Number.isInteger(parsed) && parsed >= 1) return parsed;
        this.toastService.showError(`${label} must be a whole number greater than 0.`, 3500);
        return null;
    }

    private toPositiveInteger(value: unknown, fallback: number): number {
        const parsed = Number(value);
        if (!Number.isInteger(parsed) || parsed < 1) return fallback;
        return parsed;
    }

    private defaultTimezone(): string {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Oslo';
        } catch {
            return 'Europe/Oslo';
        }
    }

    private formatSeriesError(err: any): string {
        const code = err?.error?.error || err?.message || '';
        const labels: Record<string, string> = {
            timezone_invalid: 'Timezone is not a valid IANA timezone.',
            timezone_required: 'Timezone is required.',
            starts_on_invalid: 'Start date is invalid.',
            ends_before_start: 'End date must be on or after the start date.',
            end_condition_required: 'Choose an end date or occurrence count.',
            occurrence_limit_exceeded: 'The recurrence creates too many occurrences.',
        };
        return labels[code] || 'Recurring webinar series could not be saved.';
    }

    private parseDateOnly(iso: string | null): Date | null {
        if (!iso) return null;
        const date = new Date(iso);
        if (Number.isNaN(date.valueOf())) return null;
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private formatTime(iso: string | null): string {
        if (!iso) return '';
        const date = new Date(iso);
        if (Number.isNaN(date.valueOf())) return '';
        const pad = (val: number) => val.toString().padStart(2, '0');
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    private formatModerationAction(action: WebinarChatModerationAction): string {
        switch (action) {
            case 'hide':
                return 'hidden';
            case 'unhide':
                return 'unhidden';
            case 'pin':
                return 'pinned';
            case 'unpin':
                return 'unpinned';
            default:
                return 'updated';
        }
    }

    private formatEngagementError(err: any, fallback: string): string {
        const code = err?.error?.error || err?.message || '';
        const labels: Record<string, string> = {
            engagement_locked: 'This engagement can no longer be edited from admin.',
            engagement_type_locked: 'Engagement type cannot be changed after creation.',
            message_text_required: 'Message text is required.',
            message_text_too_long: 'Message text is too long.',
            poll_question_required: 'Poll question is required.',
            poll_options_required: 'Poll needs at least two unique options.',
            resource_required: 'Tracked link title and URL are required.',
            schedule_in_past: 'Scheduled time must be in the future.',
            schedule_local_date_invalid: 'Scheduled date is invalid.',
            schedule_local_time_ambiguous: 'Scheduled local time is ambiguous in this timezone because of DST fallback.',
            schedule_local_metadata_mismatch: 'Scheduled time does not match the selected timezone.',
            schedule_local_time_invalid: 'Scheduled time is invalid.',
            schedule_local_time_not_representable: 'Scheduled local time does not exist in this timezone.',
            schedule_scheduled_for_invalid: 'Scheduled UTC time is invalid.',
            schedule_timezone_invalid: 'Scheduled timezone is invalid.',
        };
        return labels[code] || formatHttpErrorMessage(err, fallback);
    }

    private loadExistingSlugs(): void {
        this.webinarsService
            .getWebinars()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (webinars) => {
                    this.allWebinars = webinars ?? [];
                    this.refreshSlugSet();
                },
                error: (err) => {
                    console.error('Failed to load webinars for slug validation', err);
                },
            });
    }

    private upsertCachedWebinar(webinar: Webinar): void {
        const index = this.allWebinars.findIndex((item) => item.id === webinar.id);
        if (index === -1) {
            this.allWebinars.push(webinar);
        } else {
            this.allWebinars[index] = webinar;
        }
        this.refreshSlugSet();
    }

    private refreshSlugSet(): void {
        if (!this.form) return;
        const excludeId = this.currentId;
        this.existingSlugs = new Set(
            this.allWebinars
                .filter((item) => !excludeId || item.id !== excludeId)
                .map((item) => item.slug?.toLowerCase())
                .filter((slug): slug is string => Boolean(slug)),
        );
        const slugControl = this.form.get('slug');
        if (slugControl) {
            this.slugSyncInProgress = true;
            slugControl.setValue(this.normaliseSlug(slugControl.value), {emitEvent: false});
            this.slugSyncInProgress = false;
            slugControl.updateValueAndValidity({onlySelf: true, emitEvent: false});
        }
    }

    private readonly slugUniqueValidator = (control: AbstractControl): ValidationErrors | null => {
        if (!this.form) return null;
        if (this.slugSyncInProgress) return null;
        const value = this.normaliseSlug(control.value);
        if (!value) return null;
        if (this.selectedWebinar?.slug === value) return null;
        return this.existingSlugs.has(value) ? {slugTaken: true} : null;
    };

    private readonly scheduleValidator = (control: AbstractControl): ValidationErrors | null => {
        const group = control as FormGroup;
        if (group.get('webinarKind')?.value === 'recurring') {
            return null;
        }
        const startDate = group.get('scheduledDate')?.value as Date | null;
        const startTime = group.get('scheduledTime')?.value as string | null;
        const endDate = group.get('endDate')?.value as Date | null;
        const endTime = group.get('endTime')?.value as string | null;

        if (!startDate && (startTime || endDate || endTime)) {
            return {scheduleIncomplete: true};
        }

        if ((startTime && !startDate) || (endTime && !endDate)) {
            return {scheduleIncomplete: true};
        }

        if (!startDate) return null;

        const startIso = this.combineDateAndTime(startDate, startTime);
        if (!startIso) return null;
        const endIso = this.combineDateAndTime(endDate, endTime);

        if (endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
            return {endBeforeStart: true};
        }

        return null;
    };

    private readonly speakerMembersValidator = (control: AbstractControl): ValidationErrors | null => {
        if (!(control instanceof FormArray)) return null;
        if (control.length > this.maxSpeakerMembers) {
            return {speakerLimit: true};
        }
        const emails = control.controls
            .map((row) => String(row.get('email')?.value ?? '').trim().toLowerCase())
            .filter(Boolean);
        const unique = new Set(emails);
        return unique.size !== emails.length ? {duplicateSpeakerEmail: true} : null;
    };

    private setFormPristine(): void {
        if (!this.form) return;
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.form.updateValueAndValidity({onlySelf: false, emitEvent: false});
    }
}
